import { useEffect, useMemo, useState } from "react";
import {
    getAvailableUserPlaceableComponents,
    getComponentGroupPath,
    getComponentGroupSortIndex,
} from "../data/catalog";
import { isComponentCompatibleWithRack } from "../domain/placement";
import type { ComponentDefinition, MapId, RackDefinition, ScenarioId } from "../domain/types";
import { translateEntity, useUiLanguage } from "../i18n";
import { ComponentPerformanceLine } from "./CapacitySummary";
import { setDragSession } from "./drag-session";
import { SearchIcon } from "./icons";

const CATEGORY_ORDER = ["Servers", "Switches", "Gateways", "Security", "Patch panels"] as const;

interface ComponentCatalogProps {
    readonly onPlace: (component: ComponentDefinition) => void;
    readonly onQuickPlace: (component: ComponentDefinition) => void;
    readonly rack: RackDefinition;
    readonly mapId: MapId;
    readonly scenarioId: ScenarioId;
    readonly compact?: boolean;
}

export function ComponentCatalog({
    onPlace,
    onQuickPlace,
    rack,
    mapId,
    scenarioId,
    compact = false,
}: ComponentCatalogProps): React.JSX.Element {
    const { mode, t } = useUiLanguage();
    const [query, setQuery] = useState("");
    const [category, setCategory] = useState<(typeof CATEGORY_ORDER)[number]>(CATEGORY_ORDER[0]);
    const userPlaceableComponents = useMemo(
        () => getAvailableUserPlaceableComponents(scenarioId, mapId),
        [mapId, scenarioId],
    );
    const eligibleComponents = useMemo(
        () => userPlaceableComponents.filter((component) => isComponentCompatibleWithRack(rack, component)),
        [rack, userPlaceableComponents],
    );
    const components = useMemo(() => {
        const normalized = query.trim().toLowerCase();
        return [...eligibleComponents]
            .filter((component) => {
                const matchesCategory = component.category === category;
                const localizedName = translateEntity(mode, "component", component.id, component.name);
                const matchesQuery = normalized === "" || localizedName.toLowerCase().includes(normalized);
                return matchesCategory && matchesQuery;
            })
            .sort((left, right) => {
                const groupDifference = getComponentGroupSortIndex(left.id) - getComponentGroupSortIndex(right.id);
                if (groupDifference !== 0) {
                    return groupDifference;
                }
                return translateEntity(mode, "component", left.id, left.name).localeCompare(
                    translateEntity(mode, "component", right.id, right.name),
                    "en",
                    { numeric: true, sensitivity: "base" },
                );
            });
    }, [category, eligibleComponents, mode, query]);
    const componentGroups = useMemo(() => {
        const grouped = new Map<
            string,
            { readonly id: string; readonly name: string; readonly components: ComponentDefinition[] }
        >();
        for (const component of components) {
            const directGroup = getComponentGroupPath(component.id).at(-1);
            const groupId = directGroup?.id ?? `category-${component.category.toLowerCase()}`;
            const existing = grouped.get(groupId);
            if (existing === undefined) {
                grouped.set(groupId, {
                    id: groupId,
                    name: directGroup?.name ?? component.category,
                    components: [component],
                });
            } else {
                existing.components.push(component);
            }
        }
        return [...grouped.values()];
    }, [components]);
    const eligibleCategories = useMemo(
        () => CATEGORY_ORDER.filter((item) => eligibleComponents.some((component) => component.category === item)),
        [eligibleComponents],
    );

    useEffect(() => {
        if (!eligibleCategories.includes(category)) {
            setCategory(eligibleCategories[0] ?? CATEGORY_ORDER[0]);
        }
    }, [category, eligibleCategories]);

    return (
        <section className={`catalog-panel${compact ? " is-compact" : ""}`} aria-labelledby="catalog-title">
            <header className="catalog-header">
                <h2 id="catalog-title">{t("catalogTitle")}</h2>
            </header>
            <div className="catalog-filters">
                <label className="search-field">
                    <SearchIcon />
                    <span className="sr-only">{t("searchComponents")}</span>
                    <input
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={t("searchComponents")}
                    />
                </label>
                <div className="catalog-tabs" role="tablist" aria-label={t("filterCategories")}>
                    {eligibleCategories.map((item) => (
                        <button
                            key={item}
                            type="button"
                            role="tab"
                            aria-selected={category === item}
                            className={category === item ? "is-active" : undefined}
                            onClick={() => setCategory(item)}
                        >
                            <span>{translateEntity(mode, "category", item, item)}</span>
                        </button>
                    ))}
                </div>
            </div>
            <div className="catalog-list scroll-region">
                {componentGroups.map((group) => (
                    <section className="catalog-component-group" key={group.id}>
                        <h3>{translateEntity(mode, "group", group.id, group.name)}</h3>
                        <div className="catalog-group-items">
                            {group.components.map((component) => (
                                <div
                                    key={component.id}
                                    className="catalog-item"
                                    style={{ "--component-color": component.color } as React.CSSProperties}
                                    role="button"
                                    tabIndex={0}
                                    draggable
                                    aria-label={`${t("addEquipment")} ${translateEntity(mode, "component", component.id, component.name)}`}
                                    title={t("placeWithoutDrag")}
                                    onDoubleClick={() => onQuickPlace(component)}
                                    onKeyDown={(event) => {
                                        if (event.key !== "Enter" && event.key !== " ") {
                                            return;
                                        }
                                        event.preventDefault();
                                        onPlace(component);
                                    }}
                                    onDragStart={(event) => {
                                        event.dataTransfer.effectAllowed = "copy";
                                        event.dataTransfer.setData("application/x-uptime-component", component.id);
                                        event.dataTransfer.setData(
                                            "text/plain",
                                            translateEntity(mode, "component", component.id, component.name),
                                        );
                                        setDragSession({
                                            kind: "catalog",
                                            componentId: component.id,
                                            sizeU: component.sizeU,
                                        });
                                    }}
                                    onDragEnd={() => setDragSession(null)}
                                >
                                    {component.imagePath === undefined ? (
                                        <span className="component-hardware-preview" aria-hidden="true" />
                                    ) : (
                                        <img
                                            className="component-hardware-preview"
                                            src={component.imagePath}
                                            alt=""
                                            draggable={false}
                                        />
                                    )}
                                    <div className="catalog-item-copy">
                                        <strong
                                            title={translateEntity(mode, "component", component.id, component.name)}
                                        >
                                            {translateEntity(mode, "component", component.id, component.name)}
                                        </strong>
                                        <span>
                                            {translateEntity(mode, "category", component.category, component.category)}{" "}
                                            · {component.sizeU}U
                                        </span>
                                        <ComponentPerformanceLine performance={component.performance} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                ))}
                {components.length === 0 ? <div className="empty-inline">{t("noComponents")}</div> : null}
            </div>
        </section>
    );
}
